import { AIPrefillBadge } from '@components/AIPrefillBadge'
import { Dropdown } from '@components/Dropdown/Dropdown'
import {
	EMPTY_PREFILL_PROPS,
	type PrefillableFieldProps,
} from '@hooks/usePrefillableFieldProps'
import { Stack } from '@mui/material'
import { type ReactNode, useCallback } from 'react'
import {
	type Control,
	Controller,
	type ControllerFieldState,
	type ControllerRenderProps,
	type FieldPath,
	type FieldValues,
	type RegisterOptions,
} from 'react-hook-form'

interface BaseProps<TFieldValues extends FieldValues> {
	control: Control<TFieldValues>
	name: FieldPath<TFieldValues>
	isRequired?: boolean
	label: ReactNode
	options: Record<string, string>
	prefillProps?: PrefillableFieldProps
	disabled?: boolean
	fullWidth?: boolean
	rules?: RegisterOptions<TFieldValues, FieldPath<TFieldValues>>
	externalError?: string
}

interface SingleSelectProps<
	TFieldValues extends FieldValues,
> extends BaseProps<TFieldValues> {
	multiple?: false
	onChange?: (value: string) => void
	valueType?: 'string' | 'number'
}

interface MultiSelectProps<
	TFieldValues extends FieldValues,
> extends BaseProps<TFieldValues> {
	multiple: true
	onChange?: (value: readonly string[]) => void
	valueType?: never
}

type Props<TFieldValues extends FieldValues> =
	| SingleSelectProps<TFieldValues>
	| MultiSelectProps<TFieldValues>

const REQUIRED_RULES = { required: true }
const EMPTY_ARRAY = [] as const

const isMultiSelect = <TFieldValues extends FieldValues>(
	props: Props<TFieldValues>
): props is MultiSelectProps<TFieldValues> => props.multiple === true

const SelectController = <TFieldValues extends FieldValues>(
	props: Props<TFieldValues>
) => {
	const {
		control,
		name,
		isRequired,
		label,
		options,
		prefillProps: { isPrefilled, onFieldChange } = EMPTY_PREFILL_PROPS,
		disabled,
		fullWidth = true,
		rules,
		externalError,
	} = props

	const handleChange = useCallback(
		(e: { target: { value: unknown } }, onChange: (value: unknown) => void) => {
			if (isMultiSelect(props)) {
				const newValue = e.target.value as readonly string[]

				if (onFieldChange) {
					onFieldChange(newValue, onChange)
				} else {
					onChange(newValue)
				}

				props.onChange?.(newValue)
			} else {
				const stringValue = String(e.target.value)
				const valueType = props.valueType ?? 'string'
				const newValue =
					valueType === 'number' ? Number(stringValue) : stringValue

				if (onFieldChange) {
					onFieldChange(newValue, onChange)
				} else {
					onChange(newValue)
				}

				props.onChange?.(stringValue)
			}
		},
		[props, onFieldChange]
	)

	const renderField = useCallback(
		({
			field,
			fieldState: { error },
		}: {
			field: ControllerRenderProps<TFieldValues, FieldPath<TFieldValues>>
			fieldState: ControllerFieldState
		}) => {
			const commonProps = {
				ref: field.ref,
				label,
				handleChange: (e: { target: { value: unknown } }) =>
					handleChange(e, field.onChange),
				options,
				disabled,
				required: isRequired,
				fullWidth,
				fieldError: error?.type ?? (externalError ? 'required' : undefined),
				helperText: error?.type !== 'required' ? error?.message : undefined,
			}

			return (
				<Stack direction="column" spacing={0.5} width="100%">
					{isPrefilled && (
						<Stack direction="row" alignItems="center" spacing={1}>
							<AIPrefillBadge />
						</Stack>
					)}
					{isMultiSelect(props) ? (
						<Dropdown
							{...commonProps}
							multiple
							value={
								Array.isArray(field.value)
									? (field.value as readonly string[])
									: EMPTY_ARRAY
							}
						/>
					) : (
						<Dropdown
							{...commonProps}
							value={
								field.value !== null && field.value !== undefined
									? String(field.value)
									: ''
							}
						/>
					)}
				</Stack>
			)
		},
		[
			externalError,
			label,
			handleChange,
			options,
			disabled,
			isRequired,
			fullWidth,
			isPrefilled,
			props,
		]
	)

	return (
		<Controller
			name={name}
			control={control}
			rules={{
				...(isRequired ? REQUIRED_RULES : {}),
				...rules,
			}}
			render={renderField}
		/>
	)
}

export { SelectController }
